import { environment } from '../../../environments/environment';
import { CLIENT_UA_HEADER, getClientUserAgent } from './client-user-agent.util';
import { getEntrySource, withEntrySource } from './entry-source.util';

/**
 * Cuenta visitas, con o sin sesión. Dos contadores, con la misma mecánica:
 *
 *   - La visita a la web (`recordVisitOnce`), para el panel «Soporte · Dispositivos y
 *     navegadores»: una por pestaña, al arrancar la app.
 *   - La visita a la página de un evento (`recordExperienceVisitOnce`), para la
 *     pestaña Marketing del productor: una por pestaña y por evento, cuando la
 *     experiencia ya cargó.
 *
 * Es lo que deja ver a quien entra SIN iniciar sesión (eventos que no la piden,
 * gente que mira y se va): el resto de los paneles sale de las sesiones. El backend
 * (app report, `POST report/visits` y `POST report/visits/experience`) solo suma
 * contadores por día y categoría; no se manda ni se guarda ningún identificador.
 *
 * - Una vez por pestaña: la marca queda en sessionStorage, así que recargar no
 *   suma; abrir el link de nuevo (otra pestaña, otra vez desde Instagram) sí.
 * - No cuentan el backoffice (`/home…`), la validación de boletos en puerta ni los
 *   navegadores automatizados (`navigator.webdriver`: pruebas e2e, bots).
 * - `fetch` directo y no HttpClient: va sin token (el backend no lo necesita, y con
 *   token consultaría los permisos en cada visita) y sin el aviso global de error
 *   del interceptor, que nadie tiene que ver si esto falla.
 * - Nunca rompe la carga: cualquier error se descarta.
 * - La visita a un evento dice si llegó por el link de un partner: la redirección del
 *   link abre el evento con `plid` en la URL. Solo el sí/no, nunca qué partner.
 */

export const VISIT_COUNTED_KEY = 'goliiive-visit-counted';
/** Ids de los eventos ya contados en esta pestaña. */
export const EXPERIENCE_VISITS_KEY = 'goliiive-visit-experiences';
const MAX_REMEMBERED_EXPERIENCES = 50;

/** Uso interno, no público: backoffice y validación en puerta. */
const INTERNAL_PATHS = /^\/(?:home|sale-validate)(?:\/|$)/;

export interface VisitBeaconEnv {
  fetch?: (url: string, init: RequestInit) => Promise<{ ok: boolean }>;
  navigator?: { webdriver?: boolean };
  location?: { pathname: string; search?: string };
  sessionStorage?: Pick<Storage, 'getItem' | 'setItem'> | null;
  localStorage?: Pick<Storage, 'getItem'> | null;
}

let sentInThisPage = false;
const experiencesInThisPage = new Set<number>();

function storage(get: () => Storage): Storage | null {
  try {
    return get();
  } catch {
    return null;
  }
}

function browserEnv(): VisitBeaconEnv {
  if (typeof window === 'undefined') return {};
  return {
    fetch: typeof fetch === 'function' ? (url, init) => fetch(url, init) : undefined,
    navigator: globalThis.navigator,
    location: window.location,
    sessionStorage: storage(() => window.sessionStorage),
    localStorage: storage(() => window.localStorage),
  };
}

/** ¿Esta carga cuenta como visita del público? */
export function shouldCountVisit(env: VisitBeaconEnv): boolean {
  if (!env.fetch || !env.location) return false;
  if (env.navigator?.webdriver === true) return false;
  return !INTERNAL_PATHS.test(env.location.pathname || '/');
}

/** ¿Hay una sesión guardada en el navegador? (Mismo estado que lee el interceptor de tokens.) */
export function hasStoredSession(local: VisitBeaconEnv['localStorage']): boolean {
  try {
    const state = JSON.parse(local?.getItem('sessionAuthState') || '{}');
    return !!(state?.access_token || state?.refresh_token);
  } catch {
    return false;
  }
}

function alreadyCounted(session: VisitBeaconEnv['sessionStorage']): boolean {
  try {
    return session?.getItem(VISIT_COUNTED_KEY) === '1';
  } catch {
    return false;
  }
}

function markCounted(session: VisitBeaconEnv['sessionStorage']): void {
  try {
    session?.setItem(VISIT_COUNTED_KEY, '1');
  } catch {
    // Sin sessionStorage (modo privado estricto): cuenta una vez por carga.
  }
}

function rememberedExperiences(session: VisitBeaconEnv['sessionStorage']): number[] {
  try {
    const list = JSON.parse(session?.getItem(EXPERIENCE_VISITS_KEY) || '[]');
    return Array.isArray(list) ? list.filter((id) => Number.isInteger(id)) : [];
  } catch {
    return [];
  }
}

function rememberExperience(session: VisitBeaconEnv['sessionStorage'], experienceId: number): void {
  try {
    const list = rememberedExperiences(session).filter((id) => id !== experienceId);
    list.push(experienceId);
    session?.setItem(EXPERIENCE_VISITS_KEY, JSON.stringify(list.slice(-MAX_REMEMBERED_EXPERIENCES)));
  } catch {
    // Sin sessionStorage: cuenta una vez por carga.
  }
}

/** Lo que el backend necesita para clasificar: el navegador real y el origen anotado. */
function visitHeaders(): Record<string, string> {
  return {
    'Content-Type': 'application/json',
    [CLIENT_UA_HEADER]: withEntrySource(getClientUserAgent(), getEntrySource()),
  };
}

/**
 * Manda la visita a la web si corresponde. Llamarlo después de `initClientUserAgent()`,
 * para que lleve el modelo real del teléfono. Devuelve si se contó.
 */
export async function recordVisitOnce(env: VisitBeaconEnv = browserEnv()): Promise<boolean> {
  if (sentInThisPage) return false;
  sentInThisPage = true;
  try {
    if (!shouldCountVisit(env) || alreadyCounted(env.sessionStorage)) return false;
    const response = await env.fetch!(`${environment.apiBase}report/visits`, {
      method: 'POST',
      headers: visitHeaders(),
      body: JSON.stringify({ session: hasStoredSession(env.localStorage) }),
      credentials: 'omit',
    });
    // La marca va recién con la respuesta: si falló, la próxima carga lo reintenta.
    if (!response.ok) return false;
    markCounted(env.sessionStorage);
    return true;
  } catch {
    return false;
  }
}

/** ¿La página abrió desde el link de un partner? La redirección del link agrega `plid`. */
export function arrivedByPartnerLink(location: VisitBeaconEnv['location']): boolean {
  try {
    return !!new URLSearchParams(location?.search ?? '').get('plid');
  } catch {
    return false;
  }
}

/**
 * Manda la visita a la página de un evento: una por pestaña y por evento. Llamarlo
 * cuando la experiencia ya cargó (hace falta su id) y solo en la vista pública de
 * verdad: ni la previsualización ni el enlace de prueba cuentan. Devuelve si se contó.
 */
export async function recordExperienceVisitOnce(experienceId: number, env: VisitBeaconEnv = browserEnv()): Promise<boolean> {
  if (!Number.isInteger(experienceId) || experienceId <= 0) return false;
  if (experiencesInThisPage.has(experienceId)) return false;
  if (!shouldCountVisit(env) || rememberedExperiences(env.sessionStorage).includes(experienceId)) return false;
  // Se marca recién al mandar: un intento descartado (automatizado, ya contado) no
  // bloquea a uno posterior en la misma carga.
  experiencesInThisPage.add(experienceId);
  try {
    const response = await env.fetch!(`${environment.apiBase}report/visits/experience`, {
      method: 'POST',
      headers: visitHeaders(),
      body: JSON.stringify({
        experience_id: experienceId,
        session: hasStoredSession(env.localStorage),
        // Se lee al contar, antes de que el flujo cambie la URL.
        ...(arrivedByPartnerLink(env.location) ? { partner: true } : {}),
      }),
      credentials: 'omit',
    });
    if (!response.ok) {
      experiencesInThisPage.delete(experienceId);
      return false;
    }
    rememberExperience(env.sessionStorage, experienceId);
    return true;
  } catch {
    experiencesInThisPage.delete(experienceId);
    return false;
  }
}

export function resetVisitBeaconForTests(): void {
  sentInThisPage = false;
  experiencesInThisPage.clear();
}
